import { useCallback, useEffect, useState } from 'react';
import type { CanalPedido, Evento, FiltrosPedidos, ListaPedidos as Lista, Pedido } from '../../api/ops/pedidos';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { EntradasNoContratado } from './NoContratado';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { Paginacion } from '../../componentes/Paginacion';
import { Tabla, type FilaTabla } from '../../componentes/Tabla';
import { rolesDe } from '../../logica/acceso';
import { dinero } from '../../logica/dinero';
import { estadoDeError } from '../../logica/estados';
import { haySiguiente, usePaginacionCursor } from '../../logica/paginacion';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import { FichaPedido } from './FichaPedido';
import { AYUDA, CANAL, PERMISOS, chipDeFila, cuandoEvento, filtrosDePedidos, iniciales, lineaDelPedido, lineasDe, localizador, nombreComprador, opsEntradas } from './textos';

/**
 * Pedidos (10.6): los pedidos de todos los eventos (o de uno, `?evento=`) con comprador, localizador, evento, canal y
 * total; filtros Todos · Confirmados · Sin pagar · Cancelados · Reembolso fallido sin cifras (G121); evento, canal y
 * localizador en «Buscar y filtrar»; «Anterior / Siguiente». `?pedido=` abre la ficha (destino `pedido`, G386);
 * `?filtro=sin-pagar|cancelados|fallidos` llega desde Informes. Los nombres de la página, en una sola llamada (G303).
 */
const CANALES = Object.keys(CANAL) as CanalPedido[];

export function ListaPedidos(props: PropsPantalla) {
    const { negocio, movil, membresia, nombreNegocio, navegar, busqueda, ruta } = props;
    const ops = opsEntradas(props.ops);
    const permisos = usePermisos();
    const ver = permisos.puede(PERMISOS.pedidosVer);
    const verClientes = permisos.puede(PERMISOS.clientes);
    const filtros = filtrosDePedidos(permisos.puede(PERMISOS.reembolsar));

    const [filtro, setFiltro] = useState(() => Math.max(0, filtros.findIndex((f) => f.clave === busqueda.get('filtro'))));
    const [evento, setEvento] = useState(() => busqueda.get('evento') ?? '');
    const [canal, setCanal] = useState('');
    const [buscar, setBuscar] = useState('');
    const [referencia, setReferencia] = useState<string | null>(null);
    const [errorRef, setErrorRef] = useState<string | null>(null);
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;
    const [cargada, setCargada] = useState<{ vista: string; lista: Lista | null; error: unknown } | null>(null);
    const [eventos, setEventos] = useState<readonly Evento[]>([]);
    const [nombres, setNombres] = useState<Readonly<Record<string, string>>>({});
    const [intento, setIntento] = useState(0);
    const abierto = busqueda.get('pedido');

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    const elegido = filtros[filtro] ?? filtros[0];
    const estado = elegido?.filtros.status;
    const reembolso = elegido?.filtros.refund_status;
    const vista = [elegido?.clave ?? '', evento, canal, referencia ?? '', pag.cursor ?? ''].join('|');

    useEffect(() => {
        if (!ver) {
            return;
        }
        let vivo = true;
        const consulta: FiltrosPedidos = {
            status: estado,
            refund_status: reembolso,
            event_id: evento === '' ? undefined : evento,
            channel: canal === '' ? undefined : (canal as CanalPedido),
            reference: referencia ?? undefined,
            sort: '-created_at',
            cursor: pag.cursor,
        };
        ops.listTicketOrders(negocio, consulta)
            .then((l) => {
                if (vivo) {
                    setCargada({ vista, lista: l, error: null });
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setCargada((c) => ({ vista, lista: c?.vista === vista ? c.lista : null, error: e }));
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, ver, vista, estado, reembolso, evento, canal, referencia, pag.cursor, intento]);

    // Los eventos, para elegir uno y poner su nombre (sin `entradas.eventos.ver`, sin nombre).
    useEffect(() => {
        if (!ver) {
            return;
        }
        let vivo = true;
        ops.listEvents(negocio, { limit: 100 })
            .then((l) => {
                if (vivo) {
                    setEventos(l.data);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, ver]);

    const lista = cargada?.vista === vista ? cargada.lista : null;
    const faltan = verClientes && lista !== null ? [...new Set(lista.data.map((p) => p.customer_id).filter((x): x is string => typeof x === 'string' && !(x in nombres)))] : [];
    const clavesFaltan = faltan.join(',');
    useEffect(() => {
        if (clavesFaltan === '') {
            return;
        }
        let vivo = true;
        const ids = clavesFaltan.split(',');
        ops.listCustomersByIds(negocio, ids)
            .then((l) => {
                if (vivo) {
                    setNombres((x) => ({ ...x, ...Object.fromEntries(ids.map((i) => [i, l.data.find((c) => c.id === i)?.display_name ?? ''])) }));
                }
            })
            .catch(() => {
                if (vivo) {
                    setNombres((x) => ({ ...x, ...Object.fromEntries(ids.map((i) => [i, ''])) }));
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, clavesFaltan]);

    useCabecera({ ayuda: ver ? AYUDA.pedidos : [], acciones: [] });

    const sinPermiso = <EstadoSinPermiso seccion="Pedidos" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    if (!ver) {
        return sinPermiso;
    }

    const abrir = (id: string | null) => {
        const p = new URLSearchParams(busqueda);
        if (id === null) {
            p.delete('pedido');
        } else {
            p.set('pedido', id);
        }
        navegar(`${ruta}?${p.toString()}`, true);
    };

    const buscarRef = () => {
        if (buscar.trim() === '') {
            setReferencia(null);
            setErrorRef(null);
            reiniciar();
            return;
        }
        const ref = localizador(buscar);
        if (ref === null) {
            setErrorRef('El localizador tiene 8 caracteres.');
            return;
        }
        setErrorRef(null);
        setReferencia(ref);
        reiniciar();
    };

    const porId = new Map(eventos.map((e) => [e.id, e]));
    const ahora = new Date();
    const error = cargada?.vista === vista ? cargada.error : null;
    const conFiltros = filtro !== 0 || evento !== '' || canal !== '' || referencia !== null;

    const contenido = (() => {
        if (error !== null && lista === null) {
            const e = estadoDeError(error);
            if (e.tipo === 'sin-permiso') {
                return sinPermiso;
            }
            if (e.tipo === 'no-contratado') {
                return <EntradasNoContratado propietario={membresia?.is_owner === true} navegar={navegar} movil={movil} />;
            }
            return <EstadoError seccion="los pedidos" codigoSoporte={e.codigoSoporte} onReintentar={recargar} movil={movil} />;
        }
        if (lista === null) {
            return <EstadoCargando seccion="los pedidos" />;
        }
        return (
            <>
                <Tabla
                    etiqueta="Pedidos"
                    movil={movil}
                    columnas={[
                        { clave: 'comprador', titulo: 'Comprador', ancho: 'minmax(0, 1.3fr)' },
                        { clave: 'evento', titulo: 'Evento', ancho: 'minmax(0, 1.3fr)' },
                        { clave: 'canal', titulo: 'Canal', ancho: 'minmax(0, 0.8fr)' },
                        { clave: 'total', titulo: 'Total', ancho: '170px', alinear: 'derecha' },
                    ]}
                    filas={lista.data.map((p) => filaDePedido(p, nombres, porId.get(p.event_id), ahora, movil, () => { abrir(p.id); }))}
                    vacia={
                        <div className="rv-ca-vacia">
                            <span className="rv-ca-vacia__baldosa"><Icono nombre="receipt" /></span>
                            <strong>{conFiltros ? 'No hay pedidos con estos filtros.' : 'Todavía no hay pedidos.'}</strong>
                            <span>{conFiltros ? 'Prueba con otro evento o quita algún filtro. El localizador tiene 8 caracteres.' : 'Aparecen solos con cada compra: web, widget, asistente, panel o taquilla.'}</span>
                        </div>
                    }
                />
                <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={movil} />
            </>
        );
    })();

    return (
        <div className="rv-ca-apartado">
            <Filtros etiqueta="Estado de los pedidos" items={filtros} valor={filtro} onCambio={(i) => { setFiltro(i); reiniciar(); }} />
            <details className="rv-pe-plegable" open={evento !== '' || undefined}>
                <summary>
                    <span className="rv-ca-fila__texto"><strong>Buscar y filtrar</strong><span>Evento, canal y localizador.</span></span>
                    <Icono nombre="sliders-horizontal" />
                </summary>
                <div className={`rv-pe-ajustes${movil ? ' rv-pe-ajustes--movil' : ''}`}>
                    <Desplegable
                        etiqueta="Evento"
                        valor={evento}
                        opciones={[{ valor: '', texto: 'Todos los eventos' }, ...eventos.map((e) => ({ valor: e.id, texto: [e.name ?? 'Evento', cuandoEvento(e, ahora)].filter((x) => x !== '').join(' · ') }))]}
                        onChange={(v) => { setEvento(v); reiniciar(); }}
                    />
                    <Desplegable etiqueta="Canal" valor={canal} opciones={[{ valor: '', texto: 'Todos los canales' }, ...CANALES.map((c) => ({ valor: c, texto: CANAL[c][0] }))]} onChange={(v) => { setCanal(v); reiniciar(); }} />
                    <Campo etiqueta="Localizador" tipo="search" valor={buscar} onChange={setBuscar} onEnter={buscarRef} mayusculas="characters" max={12} error={errorRef} ayuda="Escribe los 8 caracteres y pulsa Intro." />
                </div>
            </details>
            {contenido}
            <FichaPedido {...props} id={abierto} nombres={nombres} eventos={porId} onCerrar={() => { abrir(null); }} onCambiado={recargar} />
        </div>
    );
}

function filaDePedido(p: Pedido, nombres: Readonly<Record<string, string>>, e: Evento | undefined, ahora: Date, movil: boolean, onAbrir: () => void): FilaTabla {
    const nombre = nombreComprador(p, nombres);
    const chip = chipDeFila(p);
    const [canal, icono] = CANAL[p.channel];
    const roja = p.status === 'pending_payment' || p.refund_status === 'failed' || nombre === 'Cliente suprimido';
    const comprador = (
        <span className="rv-pe-comprador">
            <span className={`rv-ca-inicial${roja ? ' rv-ca-inicial--roja' : ''}`}>{iniciales(nombre)}</span>
            <span className="rv-ca-fila__texto"><strong>{nombre}</strong><span>{lineaDelPedido(p, ahora)}</span></span>
        </span>
    );
    const total = (
        <span className="rv-pe-total">
            <strong>{dinero(p.total)}</strong>
            {chip !== null && <Chip estado={chip[1]}>{chip[0]}</Chip>}
        </span>
    );
    const evento = [lineasDe(p), cuandoEvento(e, ahora)].filter((x) => x !== '').join(' · ');
    return {
        clave: p.id,
        etiqueta: `Pedido ${p.reference} de ${nombre}. Abrir`,
        onAbrir,
        celdas: {
            comprador,
            evento: <span className="rv-ca-fila__texto"><strong>{e?.name ?? 'Evento'}</strong><span>{evento}</span></span>,
            canal: <span className="rv-pe-con-icono"><Icono nombre={icono} />{canal}</span>,
            total,
        },
        tarjeta: movil ? (
            <span className="rv-pe-fila-movil">
                <span className="rv-ca-fila__texto">
                    <strong>{nombre}</strong>
                    <span>{[e?.name ?? '', lineasDe(p), canal].filter((x) => x !== '').join(' · ')}</span>
                    <span>{lineaDelPedido(p, ahora)}</span>
                </span>
                {total}
            </span>
        ) : undefined,
    };
}
